// The one bottom sheet (phones), ported from the POC (bymoses/better-web-kanban); content-agnostic: task detail and
// Usage both open in it. A vertical scroll-snap scroller over the board, transparent spacer (one screen) + panel.
// Detents are snap targets: peek (the content's `.d.peek` marker, via `peekRef`; without one the sheet opens at
// half), half (`.d.half`), full (panel top); past full `.free` turns snapping off for free momentum. Backdrop,
// island strip, cover dim and head glass are scroll-driven CSS (book.css, keyed on #sheet, .head, .bleed); this code
// only toggles classes and reads scrollTop. The element is always mounted; its classes belong to this code.
// Open state lives with the caller (the URL): `open` = key of the content to show, null = closed. A dismiss (tap on
// the backdrop, scrolled down to 0) calls `onDismiss` if the caller's state is still open (`stillOpen`, read live:
// a dismiss can land before React re-renders); back/URL closing sets `open` to null and the sheet slides out.
import { Fragment, type ReactNode, type RefObject, useEffect, useLayoutEffect, useRef, useState } from "react";
type Props = {
  open: string | null;
  label: string;
  stillOpen: () => boolean;
  onDismiss: () => void;
  /** Content for the open key; put `peekRef` on a `<div className="d peek" />` to set the peek detent. */
  children: (peekRef: RefObject<HTMLDivElement | null>) => ReactNode;
};
export function Sheet({
  open,
  label,
  stillOpen,
  onDismiss,
  children
}: Props) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const halfRef = useRef<HTMLDivElement>(null);
  const peekRef = useRef<HTMLDivElement>(null);
  // `n` re-opens the same content after a dismiss (new render → the layout effect below runs).
  const [{
    key: shown
  }, setShown] = useState<{
    key: string | null;
    n: number;
  }>({
    key: null,
    n: 0
  });
  const m = useRef({
    sheetFor: null as string | null,
    opening: false,
    rise: 0,
    peekAt: 0,
    full: 0,
    lastTop: 0,
    dir: 0,
    settle: 0 as ReturnType<typeof setTimeout> | 0
  });
  const live = useRef({
    open,
    stillOpen,
    onDismiss
  });
  live.current = {
    open,
    stillOpen,
    onDismiss
  };
  const isOpen = () => !!sheetRef.current?.classList.contains("open");
  // No debounce: the sheet stops taking touches the instant it reaches the bottom, so it can't be caught on the
  // way out and the board scrolls right away.
  const hide = () => {
    const s = sheetRef.current;
    if (!s) return;
    clearTimeout(m.current.settle);
    s.style.pointerEvents = "";
    s.classList.remove("open", "free");
    m.current.sheetFor = null;
    if (live.current.stillOpen()) live.current.onDismiss();
  };
  // Close by backdrop tap or URL: also while the opening glide runs. The glide's pending scrollTo(peek) is
  // cancelled and the opening guard (which ignores scrolls until the sheet has risen) is dropped, or the
  // slide back to 0 would never hide the sheet: it stayed "open" at 0, invisible, eating touches, ?id= kept.
  const dismiss = () => {
    const s_0 = sheetRef.current;
    if (!s_0 || !isOpen()) return;
    cancelAnimationFrame(m.current.rise);
    m.current.opening = false;
    if (s_0.scrollTop <= 2) hide();else s_0.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };
  // Visuals are scroll-driven CSS; the handler only reads scrollTop against `full`, measured once when the sheet
  // opens or the window resizes.
  const measure = () => {
    const s_1 = sheetRef.current;
    const p = panelRef.current;
    if (s_1 && p) m.current.full = p.offsetTop - (Number.parseFloat(getComputedStyle(s_1).scrollPaddingTop) || 0);
  };

  // caller → sheet. Closed by back/URL: slide down; the scroll handler hides it the moment it reaches 0.
  useEffect(() => {
    const s_2 = sheetRef.current;
    if (!s_2) return;
    if (!open) {
      dismiss();
      m.current.sheetFor = null;
      return;
    }
    if (m.current.sheetFor !== open) setShown(v => ({
      key: open,
      n: v.n + 1
    }));
  }, [open]);

  // New content rendered: open at peek (or half), gliding up from 0.
  useLayoutEffect(() => {
    const s_3 = sheetRef.current;
    const p_0 = panelRef.current;
    if (!s_3 || !p_0 || !shown || shown !== live.current.open || m.current.sheetFor === shown) return;
    m.current.sheetFor = shown;
    s_3.classList.add("open");
    s_3.style.pointerEvents = "";
    s_3.scrollTop = 0;
    m.current.opening = true;
    measure();
    const marker = peekRef.current ?? halfRef.current;
    const peek = p_0.offsetTop + (marker?.offsetTop ?? 0) + 1 - s_3.clientHeight;
    m.current.peekAt = peek;
    m.current.rise = requestAnimationFrame(() => s_3.scrollTo({
      top: peek,
      behavior: "smooth"
    }));
  });
  useEffect(() => {
    const s_4 = sheetRef.current;
    if (!s_4) return;
    const st = m.current;
    // Without scroll-driven ranges (Firefox/Zen: even with its scroll-driven animations flag on it has no
    // animation-range) the backdrop's dim follows --rise (0 closed .. 1 at full), set here; same test as book.css
    const noTimeline = !CSS.supports("(animation-range: exit 0%) and (timeline-scope: --a)");
    const onScroll = () => {
      const top = s_4.scrollTop;
      if (noTimeline) s_4.style.setProperty("--rise", String(Math.min(1, top / (st.full || 1)).toFixed(3)));
      if (top >= st.full - 1 !== s_4.classList.contains("free")) s_4.classList.toggle("free"); // free momentum past full
      st.dir = Math.sign(top - st.lastTop) || st.dir;
      st.lastTop = top;
      if (s_4.style.pointerEvents === "none") {
        // released towards closed: if it snapped back to peek instead, take touches again
        clearTimeout(st.settle);
        st.settle = setTimeout(() => {
          if (s_4.scrollTop > 2) s_4.style.pointerEvents = "";
        }, 180);
      }
      if (st.opening) {
        if (top > 20) st.opening = false;
        return;
      }
      if (top <= 2 && isOpen()) hide();
    };
    // Finger released below peek while moving down = it is going away: let touches through to the board
    // immediately, while momentum carries the sheet out.
    const onTouchEnd = () => {
      if (isOpen() && !st.opening && st.dir < 0 && s_4.scrollTop < st.peekAt - 1) {
        s_4.style.pointerEvents = "none";
        clearTimeout(st.settle);
        st.settle = setTimeout(() => {
          if (s_4.scrollTop > 2) s_4.style.pointerEvents = "";
        }, 180);
      }
    };
    // A finger on the sheet takes over from the opening glide: from now on reaching 0 closes it.
    const onTouchStart = () => {
      cancelAnimationFrame(st.rise);
      st.opening = false;
    };
    const onResize = () => isOpen() && measure();
    s_4.addEventListener("scroll", onScroll, {
      passive: true
    });
    s_4.addEventListener("touchstart", onTouchStart, {
      passive: true
    });
    s_4.addEventListener("touchend", onTouchEnd, {
      passive: true
    });
    addEventListener("resize", onResize);
    return () => {
      s_4.removeEventListener("scroll", onScroll);
      s_4.removeEventListener("touchstart", onTouchStart);
      s_4.removeEventListener("touchend", onTouchEnd);
      removeEventListener("resize", onResize);
      clearTimeout(st.settle);
    };
  }, []);

  // iOS WebKit won't touch-scroll a pointer-events:none scroller, so the scroller takes touches; a tap on the
  // backdrop (the spacer above the panel) dismisses.
  return <div id="sheet" data-t="sheet" ref={sheetRef} data-sheet={shown ?? undefined}>
			<div className="spacer" data-t="backdrop" onClick={dismiss} aria-hidden />
			<div className="panel" data-t="panel" ref={panelRef} role="dialog" aria-label={label}>
				<div ref={halfRef} className="d half" />
				{shown && <Fragment key={shown}>{children(peekRef)}</Fragment>}
			</div>
		</div>;
}